import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FEED_POSTS, INFERRED_TRAITS, FeedPost } from '../data/algorithmData';
import { addXP, loadXP } from '../data/xp';
import { syncProgress } from '../api/public';
import ModuleSummary from '../components/ModuleSummary';
import { recordStart } from '../data/progress';

type Phase = 'intro' | 'browsing' | 'reveal' | 'done';

const AD_MESSAGES = [
  'Targeted ad: Gaming headset — 30% off',
  'Sponsored: New mobile game just for you',
  'Promoted: Concert tickets in your area',
  'Ad: Food delivery — free first order',
  'Sponsored: Fitness app — try free for 30 days',
  'Promoted: Stream 1000+ shows ad-free',
];

export default function AlgorithmMirror() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('intro');
  const [clicked, setClicked] = useState<Set<string>>(new Set());
  const [profileVisible, setProfileVisible] = useState(false);
  const [currentAd, setCurrentAd] = useState(0);
  const [adFlash, setAdFlash] = useState(false);

  const activeTraits = INFERRED_TRAITS.filter(trait =>
    trait.triggeredBy.some(id => clicked.has(id))
  );

  function handleClick(post: FeedPost) {
    const wasEmpty = clicked.size === 0;
    setClicked(prev => { const next = new Set(Array.from(prev)); next.add(post.id); return next; });
    if (wasEmpty) setProfileVisible(true);

    // flash a new ad
    setAdFlash(true);
    setCurrentAd(Math.floor(Math.random() * AD_MESSAGES.length));
    setTimeout(() => setAdFlash(false), 1500);
  }

  function finishBrowsing() {
    setPhase('reveal');
    if (clicked.size >= 3) {
      addXP(100, 'algorithm_mirror');
      recordStart();
      syncProgress('algorithm_mirror', 100);
    }
  }

  const profileStrength = Math.min(100, clicked.size * 12);

  if (phase === 'intro') return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 580, textAlign: 'center' }}>
        <div style={{ fontSize: 64, marginBottom: 16 }}>🔍</div>
        <h1 style={{ fontSize: 36, fontWeight: 800, color: '#1e1b4b', marginBottom: 12 }}>Algorithm Mirror</h1>
        <p style={{ fontSize: 17, color: '#374151', lineHeight: 1.7, marginBottom: 12 }}>
          You're about to see a simulated social media feed. <strong>Just browse normally</strong> — click on content that interests you.
        </p>
        <p style={{ fontSize: 17, color: '#374151', lineHeight: 1.7, marginBottom: 28 }}>
          On the right side, watch as an algorithm builds a profile of you in real time — inferring your interests, demographics, and mood — the same way real platforms do.
        </p>
        <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 12, padding: 16, marginBottom: 28, textAlign: 'left' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>⚡ How it works in real life</div>
          <p style={{ fontSize: 14, color: '#78350f', lineHeight: 1.6, margin: 0 }}>
            Real algorithms don't need you to click. They track: scroll speed (slowing down = interest), hover time, re-reads, share intent, and even mouse movement patterns.
          </p>
        </div>
        <button onClick={() => setPhase('browsing')} style={{
          background: 'linear-gradient(135deg, #3730a3, #4f46e5)',
          color: 'white', border: 'none', borderRadius: 12,
          padding: '14px 32px', fontSize: 16, fontWeight: 700, cursor: 'pointer',
        }}>
          Open the feed →
        </button>
      </div>
    </div>
  );

  if (phase === 'browsing') return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px', display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'start' }}>

        {/* Feed column */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#1e1b4b', margin: 0 }}>Your Feed</h2>
              <p style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>Click on anything that interests you</p>
            </div>
            {clicked.size >= 3 && (
              <button onClick={finishBrowsing} style={{
                background: '#10b981', color: 'white', border: 'none', borderRadius: 10,
                padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}>
                See my profile →
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {FEED_POSTS.map(post => {
              const isClicked = clicked.has(post.id);
              return (
                <button key={post.id} onClick={() => handleClick(post)} style={{
                  background: isClicked ? '#eef2ff' : 'white',
                  border: `2px solid ${isClicked ? '#6366f1' : '#e5e7eb'}`,
                  borderRadius: 16,
                  padding: '16px 20px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  gap: 14,
                  alignItems: 'flex-start',
                  transition: 'all 0.15s',
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: 12,
                    background: isClicked ? '#e0e7ff' : '#f3f4f6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 24, flexShrink: 0,
                  }}>
                    {post.emoji}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>
                      {post.account} · {post.type}
                    </div>
                    <div style={{ fontSize: 15, color: '#1e1b4b', fontWeight: 500, lineHeight: 1.4 }}>
                      {post.content}
                    </div>
                    {isClicked && (
                      <div style={{ fontSize: 12, color: '#6366f1', fontWeight: 600, marginTop: 6 }}>
                        ✓ Logged by algorithm
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          <div style={{ textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: 14 }}>
            — You've reached the end of your personalised feed —
          </div>
        </div>

        {/* Profile panel */}
        <div style={{ position: 'sticky', top: 80 }}>
          <div style={{
            background: profileVisible ? 'white' : '#f9fafb',
            borderRadius: 20,
            padding: 24,
            boxShadow: profileVisible ? '0 4px 20px rgba(0,0,0,0.1)' : 'none',
            border: `2px solid ${profileVisible ? '#6366f1' : '#e5e7eb'}`,
            transition: 'all 0.3s',
          }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1e1b4b', marginBottom: 4 }}>
              🤖 Your Algorithm Profile
            </h3>
            <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>
              Built from {clicked.size} click{clicked.size !== 1 ? 's' : ''} — updates in real time
            </p>

            {!profileVisible ? (
              <div style={{ textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 14 }}>
                Start clicking to see your profile build...
              </div>
            ) : (
              <>
                {/* Profile strength */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
                    <span>Profile completeness</span>
                    <span style={{ fontWeight: 700, color: '#6366f1' }}>{profileStrength}%</span>
                  </div>
                  <div style={{ height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${profileStrength}%`, height: '100%', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', transition: 'width 0.4s', borderRadius: 4 }} />
                  </div>
                </div>

                {/* Inferred traits */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>
                    Inferred interests
                  </div>
                  {activeTraits.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#9ca3af' }}>Click more posts to reveal...</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {activeTraits.map(t => (
                        <span key={t.label} style={{
                          background: '#eef2ff', color: '#3730a3',
                          padding: '4px 12px', borderRadius: 20,
                          fontSize: 13, fontWeight: 600,
                          display: 'flex', alignItems: 'center', gap: 4,
                          animation: 'fadeIn 0.3s',
                        }}>
                          {t.icon} {t.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Targeted ads */}
                {activeTraits.length > 0 && (
                  <div style={{
                    background: adFlash ? '#fef3c7' : '#f3f4f6',
                    border: `1px solid ${adFlash ? '#fbbf24' : '#e5e7eb'}`,
                    borderRadius: 10,
                    padding: 12,
                    transition: 'all 0.2s',
                    marginBottom: 12,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', marginBottom: 4 }}>📢 Ads targeting you now</div>
                    <div style={{ fontSize: 13, color: '#374151' }}>{AD_MESSAGES[currentAd]}</div>
                  </div>
                )}

                {/* Ad categories */}
                {activeTraits.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                      You'd be sold to
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {activeTraits.slice(0, 4).map(t => (
                        <div key={t.label} style={{ fontSize: 12, color: '#6b7280', display: 'flex', gap: 6 }}>
                          <span>→</span>
                          <span>{t.adCategory}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {clicked.size >= 3 && (
            <button onClick={finishBrowsing} style={{
              width: '100%', marginTop: 12,
              background: '#10b981', color: 'white', border: 'none', borderRadius: 12,
              padding: '12px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
            }}>
              See the full reveal →
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (phase === 'reveal') {
    const xpState = loadXP();
    return (
      <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '48px 24px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🪞</div>
            <h1 style={{ fontSize: 32, fontWeight: 800, color: '#1e1b4b', marginBottom: 8 }}>Here's what the algorithm knows</h1>
            <p style={{ color: '#6b7280', fontSize: 16 }}>From {clicked.size} clicks. Imagine what it knows after months of use.</p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            {activeTraits.map(t => (
              <div key={t.label} style={{ background: 'white', borderRadius: 16, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>{t.icon}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1e1b4b', marginBottom: 4 }}>{t.label}</div>
                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>Would sell you: <strong>{t.adCategory}</strong></div>
              </div>
            ))}
          </div>

          <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 16, padding: 24, marginBottom: 24 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#991b1b', marginBottom: 8 }}>⚠ What they can infer beyond your clicks</h3>
            <ul style={{ fontSize: 14, color: '#7f1d1d', lineHeight: 1.8, paddingLeft: 20, margin: 0 }}>
              <li>Approximate age and gender from content preferences</li>
              <li>Income bracket from brand interactions</li>
              <li>Emotional state from time-of-day and scroll speed</li>
              <li>Location from content timezone and language</li>
              <li>Relationship status from social engagement patterns</li>
              <li>Political leanings from news content engagement</li>
            </ul>
          </div>

          <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 16, padding: 24, marginBottom: 32 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#065f46', marginBottom: 8 }}>✓ How to fight back</h3>
            <ul style={{ fontSize: 14, color: '#064e3b', lineHeight: 1.8, paddingLeft: 20, margin: 0 }}>
              <li>Turn off personalised ads in every platform's settings</li>
              <li>Use a browser extension like uBlock Origin or Privacy Badger</li>
              <li>Switch to a privacy-first browser (Firefox, Brave)</li>
              <li>Use a VPN to mask your IP address</li>
              <li>Request data deletion from companies under PIPEDA</li>
            </ul>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#10b981', marginBottom: 4 }}>+100 XP earned</div>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>Algorithm Mirror complete · Total: {xpState.total} XP</div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
              <button onClick={() => navigate('/quest')} style={{
                background: '#3730a3', color: 'white', border: 'none', borderRadius: 12,
                padding: '12px 24px', fontSize: 15, fontWeight: 700, cursor: 'pointer',
              }}>Play Privacy Quest →</button>
            </div>
            <ModuleSummary moduleId="algorithm" xpEarned={100} />
          </div>
        </div>
      </div>
    );
  }

  return null;
}
